import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AuthShell from "@/components/layout/AuthShell";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { REGISTER } from "@/constants/testIds";
import { Loader2, AlertCircle } from "lucide-react";
import { Brand } from "@/components/Brand";

export default function SignUp() {
  const { register, errMsg } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "", phone: "", smsOptIn: false });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirm) return setError("Passwords do not match.");
    if (form.password.length < 8) return setError("Password must be at least 8 characters.");
    setLoading(true);
    try {
      await register(form.name, form.email, form.password, {
        phone: form.phone.trim() || undefined,
        sms_opt_in: form.smsOptIn,
      });
      navigate("/organizer/onboarding", { replace: true });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title={<>Create your <Brand /> account</>}
      subtitle="Organizer accounts are required to start a fundraiser. It's free."
      footer={<>Already have an account? <Link to="/sign-in" data-testid={REGISTER.loginLink} className="font-semibold text-ff-red hover:underline">Sign in</Link></>}
    >
      <form onSubmit={submit} className="space-y-5" data-testid="signup-form">
        {error && (
          <Alert variant="destructive" data-testid="signup-error">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <div>
          <Label htmlFor="name">Full legal name</Label>
          <Input id="name" required data-testid={REGISTER.nameInput} value={form.name} onChange={set("name")}
            className="mt-1.5" placeholder="Sarah Mitchell" autoComplete="name" />
        </div>
        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" required data-testid={REGISTER.emailInput} value={form.email}
            onChange={set("email")} className="mt-1.5" placeholder="you@example.com" autoComplete="email" />
        </div>
        <div>
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" required data-testid={REGISTER.passwordInput} value={form.password}
            onChange={set("password")} className="mt-1.5" placeholder="At least 8 characters" autoComplete="new-password" />
        </div>
        <div>
          <Label htmlFor="confirm">Confirm password</Label>
          <Input id="confirm" type="password" required data-testid={REGISTER.passwordConfirmInput} value={form.confirm}
            onChange={set("confirm")} className="mt-1.5" autoComplete="new-password" />
        </div>
        <div>
          <Label htmlFor="phone">Mobile number <span className="font-normal text-ff-medium">(optional)</span></Label>
          <Input id="phone" type="tel" data-testid="signup-phone-input" value={form.phone}
            onChange={set("phone")} className="mt-1.5" placeholder="+1 (555) 123-4567" autoComplete="tel" />
          <label className="mt-3 flex cursor-pointer items-start gap-2.5" data-testid="signup-sms-optin-label">
            <Checkbox checked={form.smsOptIn} onCheckedChange={(v) => setForm({ ...form, smsOptIn: !!v })}
              data-testid="signup-sms-optin" className="mt-0.5" />
            <span className="text-xs leading-relaxed text-ff-medium">
              Text me occasional account & fundraiser updates. Consent isn't required to sign up. Msg &amp; data rates may apply; reply STOP to opt out.
            </span>
          </label>
        </div>
        <Button type="submit" disabled={loading} data-testid={REGISTER.submitButton}
          className="w-full bg-ff-action font-semibold text-white hover:bg-ff-actionDark">
          {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating account</> : "Create Account"}
        </Button>
      </form>
    </AuthShell>
  );
}
